/**
 * SC Modal - A simple vanilla JavaScript modal component
 */
class SCModal {
  constructor(options = {}) {
    this.options = {
      size: options.size || 'medium', // small, medium, large
      closeOnBackdropClick: options.closeOnBackdropClick !== undefined ? options.closeOnBackdropClick : true,
      content: options.content || '',
      title: options.title || '',
      footer: options.footer || '',
      id: options.id || `sc-modal-${Math.floor(Math.random() * 1000)}`,
      showLoader: options.showLoader || false // New option for loader
    };
    
    this.modal = null;
    this.backdrop = null;
    this.loader = null;
    this.isOpen = false;
    this.isLoading = false;
    
    this.init();
  }
  
  init() {
    // Create modal elements
    this.modal = document.createElement('div');
    this.modal.className = `sc-modal sc-modal-${this.options.size}`;
    this.modal.id = this.options.id;
    this.modal.style.display = 'none';
    
    // Create backdrop
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'sc-modal-backdrop';
    this.backdrop.style.display = 'none';
    
    // Build modal structure
    this.modal.innerHTML = `
      <div class="sc-modal-content">
        <div class="sc-modal-header">
          <h2 class="sc-modal-title">${this.options.title}</h2>
          <button type="button" class="sc-modal-close">
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30" fill="none">
              <path d="M15 27.5C8.09644 27.5 2.5 21.9035 2.5 15C2.5 8.09644 8.09644 2.5 15 2.5C21.9035 2.5 27.5 8.09644 27.5 15C27.5 21.9035 21.9035 27.5 15 27.5ZM15 13.2323L11.4645 9.6967L9.6967 11.4645L13.2323 15L9.6967 18.5355L11.4645 20.3033L15 16.7678L18.5355 20.3033L20.3033 18.5355L16.7678 15L20.3033 11.4645L18.5355 9.6967L15 13.2323Z" fill="#113E53" fill-opacity="0.6"/>
            </svg>
          </button>
        </div>
        <div class="sc-modal-body">
          ${this.options.content}
        </div>
        ${this.options.footer ? `<div class="sc-modal-footer">${this.options.footer}</div>` : ''}
      </div>
      <div class="sc-modal-loader">
        <div class="sc-modal-spinner"></div>
      </div>
    `;
    
    // Get the loader element
    this.loader = this.modal.querySelector('.sc-modal-loader');
    
    // Initialize loader state
    if (this.options.showLoader) {
      this.showLoader();
      console.log('show loader');
    } else {
      this.hideLoader();
    }
    
    // Add event listeners
    const closeBtn = this.modal.querySelector('.sc-modal-close');
    closeBtn.addEventListener('click', () => this.close());
    
    if (this.options.closeOnBackdropClick) {
      this.backdrop.addEventListener('click', () => this.close());
    }
    
    // Append to DOM
    document.body.appendChild(this.backdrop);
    document.body.appendChild(this.modal);
  }
  
  /**
   * Open the modal
   */
  open() {
    if (this.isOpen) return;
    
    this.backdrop.style.display = 'block';
    this.modal.style.display = 'block';
    
    // Add animation classes
    setTimeout(() => {
      this.backdrop.classList.add('active');
      this.modal.classList.add('active');
    }, 10);
    
    this.isOpen = true;
    
    // Prevent body scrolling
    document.body.style.overflow = 'hidden';
    
    // Trigger event
    document.dispatchEvent(new CustomEvent('sc-modal-opened', { 
      detail: { id: this.options.id }
    }));
  }
  
  /**
   * Close the modal
   */
  close() {
    if (!this.isOpen) return;
    
    // Remove animation classes
    this.backdrop.classList.remove('active');
    this.modal.classList.remove('active');
    
    // Wait for animation to complete
    setTimeout(() => {
      this.backdrop.style.display = 'none';
      this.modal.style.display = 'none';
    }, 300);
    
    this.isOpen = false;
    
    // Restore body scrolling
    document.body.style.overflow = '';
    
    // Trigger event
    document.dispatchEvent(new CustomEvent('sc-modal-closed', { 
      detail: { id: this.options.id }
    }));
  }
  
  /**
   * Check if modal is open
   * @return {boolean} True if modal is open
   */
  isModalOpen() {
    return this.isOpen;
  }
  
  /**
   * Show loader overlay in the modal
   */
  showLoader() {
    if (!this.loader) return;
    
    this.loader.style.display = 'flex';
    this.isLoading = true;
    
    // Trigger event
    document.dispatchEvent(new CustomEvent('sc-modal-loader-shown', { 
      detail: { id: this.options.id }
    }));
  }
  
  /**
   * Hide loader overlay in the modal
   */
  hideLoader() {
    if (!this.loader) return;
    
    this.loader.style.display = 'none';
    this.isLoading = false;
    
    // Trigger event
    document.dispatchEvent(new CustomEvent('sc-modal-loader-hidden', { 
      detail: { id: this.options.id }
    }));
  }
  
  /**
   * Toggle loader visibility
   * @param {boolean} show - Whether to show or hide the loader
   */
  toggleLoader(show) {
    if (show) {
      this.showLoader();
    } else {
      this.hideLoader();
    }
  }
  
  /**
   * Check if loader is visible
   * @return {boolean} True if loader is visible
   */
  isLoading() {
    return this.isLoading;
  }
  
  /**
   * Update modal content
   * @param {string} content - HTML content
   */
  setContent(content) {
    this.modal.querySelector('.sc-modal-body').innerHTML = content;
  }
  
  /**
   * Update modal title
   * @param {string} title - Title text
   */
  setTitle(title) {
    this.modal.querySelector('.sc-modal-title').innerHTML = title;
  }
  
  /**
   * Update modal footer
   * @param {string} footer - Footer HTML content
   */
  setFooter(footer) {
    let footerElement = this.modal.querySelector('.sc-modal-footer');
    
    if (!footer && footerElement) {
      footerElement.remove();
    } else if (footer && footerElement) {
      footerElement.innerHTML = footer;
    } else if (footer) {
      footerElement = document.createElement('div');
      footerElement.className = 'sc-modal-footer';
      footerElement.innerHTML = footer;
      this.modal.querySelector('.sc-modal-content').appendChild(footerElement);
    }
  }
  
  /**
   * Destroy the modal and remove from DOM
   */
  destroy() {
    this.modal.remove();
    this.backdrop.remove();
  }
}

// Make it available globally
window.SCModal = SCModal;
